<template>
  <div class="result w-full">

    <!-- GENERAL INFO -->

    <div id="info-card" class="bg-white px-4 py-6 mb-2">
      <div class="flex flex-row justify-between items-center">
        <h1 class="font-semibold text-2xl line-clamp-1">
          {{ userName }}
        </h1>
        <span class="bg-[#EBD4EF] text-[#9C27B0] text-xxs font-medium px-2 py-1 rounded border border-[#9C27B0] whitespace-nowrap capitalize">{{ lifeStageString }}</span>
      </div>
      <div class="mt-4 grid grid-cols-2 gap-4">
        <div class="age">
          <p class="text-sm my-1">Usia</p>
          <p class="text-base my-1 font-semibold">{{ userAge.number }} {{ userAge.type }}</p>
        </div>
        <div v-if="lifeStageString === 'Ibu Hamil'" class="pregnancy">
          <p class="text-sm my-1">Usia Kehamilan</p>
          <p class="text-base my-1 font-semibold">{{ screeningResult?.observation?.pregnancyAge || '-' }} Minggu</p>
        </div>
        <div v-if="lifeStageString === 'Ibu Hamil'" class="children">
          <p class="text-sm my-1">Jumlah Anak</p>
          <p class="text-base my-1 font-semibold">{{ screeningResult?.observation?.pregnancyTo }}</p>
        </div>
        <div v-if="lifeStageString === 'Ibu Hamil'" class="gap">
          <p class="text-sm my-1">Jarak Kehamilan</p>
          <p class="text-base my-1 font-semibold">{{ pregnancyInterval }}</p>
        </div>
        <div v-if="lifeStageString === 'Bulin & Bufas'" class="gap">
          <p class="text-sm my-1">Kelahiran Ke</p>
          <p class="text-base my-1 font-semibold">{{ screeningResult?.childbirthInformation?.nthBirth }}</p>
        </div>
      </div>
      <hr/>
      <div class="mt-4 grid grid-cols-2 gap-4">
        <div class="age">
          <p class="text-sm my-1">Tempat Pemeriksaan</p>
          <p class="text-base my-1 font-semibold">{{ screeningResult?.activity?.name }}</p>
        </div>
        <div class="pregnancy">
          <p class="text-sm my-1">Tanggal Pemeriksaan</p>
          <p class="text-base my-1 font-semibold">{{ screeningDate }}</p>
        </div>
      </div>
    </div>

    <!-- SUMMARY -->

    <div id="summary-card" class="bg-white px-4 py-6 mb-2">
      <BaseAccordion :color="summary.status === 1 ? 'warning-dark' : 'danger-dark'" icon :class="summary.status === 1 ? 'border-warning-darker' : 'border-danger-darker'" v-if="summary.status !== 0">
        <template #title>Rangkuman Pemeriksaan</template>
        <template #desc>
          <p class="text-sm font-semibold mt-2"
          :class="summary.status === 1 ? 'text-warning-darker ' : 'text-danger-darker'">
            {{ summary.desc }}
          </p>
        </template>
        <template #body>
          <ul class="list-accordion">
            <li>
              Lakukan pemeriksaan secara mandiri apabila Anda mampu.
            </li>
            <li>
              Hubungi Kader bila anda merasa kebingungan atau memerlukan bantuan.
            </li>
          </ul>
        </template>
      </BaseAccordion>

      <div class="border-success-darker border-[1px] p-4 rounded-lg w-100 flex justify-between" v-else>
        <p class="text-sm font-semibold text-success-darker">
          {{ summary.desc }}
        </p>
        <span class="icons flex flex-row items-center">
          <Circle class="w-6 h-6 me-3" :class="`bg-success-dark`"/>
        </span>
      </div>

      <hr class="my-5"/>

      <p class="text-sm my-3">Keterangan Status</p>
      <div class="status-chips flex flex-row justify-between items-center gap-2">
        <span class="status-chip status-chip-good">Baik</span>
        <span class="status-chip status-chip-warn">Awasi</span>
        <span class="status-chip status-chip-danger">Laporkan</span>
      </div>
    </div>

    <!-- TANDA KESEHATAN -->

    <div id="health-mark-card" class="info-card bg-white px-4 py-6 mb-2">
      <h3 class="text-xl font-semibold mb-4">Tanda Kesehatan</h3>
      <BaseAccordion
        v-for="(item, index) in resultGroup.healthSign"
        :key="`health-sign-${index}`"
        :color="`${item.status}-dark`" icon
        class="mb-2"
        :class="item.status !== 'normal' && `border-${item.status}-darker`">
        <template #title>{{ healthSignTitle[index] }}</template>
        <template #body>
          <p class="text-sm font-semibold mt-2" :class="`text-${item.status}-darker`">
            {{ item.value }} {{ item.unit }} {{ item.value && item.text && '-' }} {{ item.text }}
          </p>
        </template>
      </BaseAccordion>
    </div>

    <!-- RISIKO -->

    <div id="risk-card" class="info-card bg-white px-4 py-6 mb-2">
      <h3 class="text-xl font-semibold mb-4">Beresiko</h3>
      <BaseAccordion
        v-for="(item, index) in resultGroup.risk"
        :key="`risk-${index}`"
        :color="`${item.status}-dark`" icon
        class="mb-2"
        :class="item.status !== 'normal' && `border-${item.status}-darker`">
        <template #title>{{ riskTitle[index] }}</template>
        <template #body>
          <p
            class="text-sm font-semibold mt-2"
            :class="`text-${item.status}-darker`"
          >
            {{ item.value }} {{ item.unit }} {{ item.value && item.text && '-' }} {{ item.text }}
          </p>
          <!-- Lihat Detail Risiko Kehamilan, Pasca Melahirkan, Bayi -->
          <router-link
            v-if="(item.menu === 'pregnancyRisk' || item.menu === 'postpartumRisk' || item.menu === 'babyRisk') &&
            item.status !== 'normal'"
            :to="`/risk?token=${encodeURIComponent(token)}${route?.query?.page ? `&page=${route?.query?.page}` : ''}`"
          >
            <p class="text-sm font-semibold mt-2 inline-block" :class="`text-danger-darker`">
              <span class="underline me-3">Lihat Detail</span>
              <ArrowRightIcon color="#B72424" class="w-5 h-5 inline-block"/>
            </p>
          </router-link>

          <!-- Lihat Detail SKILAS Lansia -->
          <router-link
            v-if="item.menu === 'skilas' && item.status !== 'normal'"
            :to="`/skilas?token=${encodeURIComponent(token)}${route?.query?.page ? `&page=${route?.query?.page}` : ''}`"
          >
            <p class="text-sm font-semibold mt-2 inline-block" :class="`text-${item.status}-darker`">
              <span class="underline me-3">Lihat Detail</span>
              <ArrowRightIcon :color="item.status" class="w-5 h-5 inline-block"/>
            </p>
          </router-link>

          <!-- Lihat Detail AKS Lansia -->
          <router-link
            v-if="item.menu === 'aks'"
            :to="`/aks?token=${encodeURIComponent(token)}${route?.query?.page ? `&page=${route?.query?.page}` : ''}`"
          >
            <p class="text-sm font-semibold mt-2 inline-block" :class="`text-${item.status}-darker`">
              <span class="underline me-3">Lihat Detail</span>
              <ArrowRightIcon :color="item.status" class="w-5 h-5 inline-block"/>
            </p>
          </router-link>
        </template>
      </BaseAccordion>
    </div>

    <!-- STATUS -->

    <div id="status-card" class="info-card bg-white px-4 py-6 mb-2">
      <h3 class="text-xl font-semibold mb-4">Status</h3>
      <!-- Danger Stat -->
      <BaseAccordion
        v-for="(item, index) in Object.keys(reminderGrouped?.danger || {})"
        :key="`statusDanger-${index}`"
        color="danger-dark" icon
        class="mb-2 border-danger-darker">
        <template #title>{{ getReminderTitle(item, lifeStageString) }}</template>
        <template #body>
          <div v-for="(item2, index2) in reminderGrouped.danger[item]" :key="`statusText-${index}-${index2}`">
            <hr v-if="index2 > 0" class="my-2"/>
            <p class="text-sm font-semibold mt-2 text-danger-darker">{{ item2.text }}</p>
          </div>
        </template>
      </BaseAccordion>
      <!-- Warning Stat -->
      <BaseAccordion
        v-for="(item, index) in Object.keys(reminderGrouped?.warning || {})"
        :key="`statusWarn-${index}`"
        color="warning-dark" icon
        class="mb-2 border-warning-darker">
        <template #title>{{ getReminderTitle(item, lifeStageString) }}</template>
        <template #body>
          <div v-for="(item2, index2) in reminderGrouped.warning[item]" :key="`statusText-${index}-${index2}`">
            <hr v-if="index2 > 0" class="my-2"/>
            <p class="text-sm font-semibold mt-2 text-warning-darker">{{ item2.text }}</p>
          </div>
        </template>
      </BaseAccordion>
      <!-- Normal Stat -->
      <div
        v-for="(item, index) in Object.keys(reminderGrouped?.normal || {})"
        :key="`statusNormal-${index}`"
        class="mb-2 border-[1px] p-4 rounded-lg border-[#c3c3c3]">
        <p class="text-base font-semibold">{{ getReminderTitle(item, lifeStageString) }}</p>
        <div v-for="(item2, index2) in reminderGrouped.normal[item]" :key="`statusTextNormal-${index}-${index2}`">
          <hr v-if="index2 > 0" class="my-2"/>
          <p class="text-sm font-semibold mt-2 text-normal-darker">{{ item2.text }}</p>
        </div>
      </div>
    </div>

    <!-- Footer -->

    <div id="detail-button" class="bg-white rounded-none py-4 px-4 font-semibold shadow-[rgba(0,0,15,0.1)_0px_-4px_4px_0px] sticky bottom-0">
      <router-link :to="`/detail-result?token=${encodeURIComponent(token)}`">
        <BaseButton>
          <template #text>
            Lihat Detail Individu
          </template>
        </BaseButton>
      </router-link>
    </div>

    <Loading v-show="loading" class="top-0 left-0 bg-[rgba(0,0,0,0.25)]"/>
  </div>
</template>

<script setup>
import {
  ref, watch, computed,
} from 'vue';

import { useStore } from 'vuex';
import { useRoute } from 'vue-router';

import BaseAccordion from '@/components/accordion/BaseAccordion.vue';
import Loading from '@/components/LoadingOverlay.vue';

import ArrowRightIcon from '@/components/icons/ArrowRight.vue';
import BaseButton from '@/components/buttons/BaseButton.vue';
import Circle from '@/components/icons/Circle.vue';

import { getAge, formatDashDate } from '@/utilities/helpers';
import {
  getPregnancyInterval, lifeStageType, getHealthSignTitle, getRiskTitle, getReminderTitle,
} from '@/utilities/constants';

const store = useStore();
const route = useRoute();
const date = ref(null);
const dateEmpty = ref(false);
const token = ref(route?.query?.token || '');

const loading = computed(() => {
  const loadingGlobal = store.state.global.loading;
  return loadingGlobal;
});

const userName = computed(() => {
  const { name } = store.state.global;
  return name;
});

const userAge = computed(() => {
  const { dob } = store.state.global;
  return getAge(dob);
});

const screeningResult = computed(() => {
  let result = store.state.screening.screeningData[store.state.screening.screeningIndex];
  if (route?.query?.page) {
    result = store.state.screening.screeningData[route?.query?.page - 1];
    store.dispatch('screening/setScreeningIndex', route?.query?.page - 1);
  }
  return result;
});

const resultGroup = computed(() => {
  if (screeningResult.value?.results?.length > 0) {
    return Object.groupBy(screeningResult.value?.results, ({ type }) => type);
  }
  return {
    healthSign: [],
    reminder: [],
    risk: [],
  };
});

const reminderGrouped = computed(() => {
  if (resultGroup.value?.reminder?.length > 0) {
    const statGroup = resultGroup.value?.reminder.reduce((acc, item) => {
      (acc[item.status] = acc[item.status] || []).push(item);
      return acc;
    }, {});

    const grouped = { normal: {}, warning: {}, danger: {} };
    grouped.normal = statGroup?.normal?.reduce((acc, item) => {
      (acc[item.menu] = acc[item.menu] || []).push(item);
      return acc;
    }, {});

    grouped.warning = statGroup?.warning?.reduce((acc, item) => {
      (acc[item.menu] = acc[item.menu] || []).push(item);
      return acc;
    }, {});

    grouped.danger = statGroup?.danger?.reduce((acc, item) => {
      (acc[item.menu] = acc[item.menu] || []).push(item);
      return acc;
    }, {});

    return grouped;
  }
  return { normal: {}, warning: {}, danger: {} };
});

const lifeStageString = computed(() => lifeStageType(screeningResult.value?.lifeStage, screeningResult.value?.pregnancyStatus));

const pregnancyInterval = computed(() => getPregnancyInterval(screeningResult.value?.observation.pregnancyInterval - 1 || ''));

const screeningDate = computed(() => formatDashDate(screeningResult.value?.date || ''));

const summary = computed(() => {
  // 0 good, 1 warning, 2 danger
  let status = 0;
  resultGroup.value?.healthSign?.forEach((item) => {
    if (item.status === 'danger') {
      status = 2;
      return false;
    }
    if (item.status === 'warning') {
      status = 1;
    }
  });
  let desc = '-';
  switch (status) {
    case 0:
      desc = 'Semua hasil pemeriksaan baik, jaga selalu kesehatan Anda.';
      break;
    case 1:
      desc = 'Beberapa tanda kesehatan dan resiko perlu lebih diperhatikan.';
      break;
    case 2:
      desc = 'Segera kunjungi Puskesmas atau layanan kesehatan lainnya.';
      break;
    default:
      desc = '-';
      break;
  }
  return {
    status,
    desc,
  };
});

const healthSignTitle = computed(() => {
  if (resultGroup.value?.healthSign?.length > 0) {
    return getHealthSignTitle(resultGroup.value.healthSign, lifeStageString.value);
  }
  return [];
});

const riskTitle = computed(() => {
  if (resultGroup.value?.risk?.length > 0) {
    return getRiskTitle(resultGroup.value.risk, lifeStageString);
  }
  return [];
});

async function getFamilyData() {
  const res = await store.dispatch('screening/getFamilyData', {
    token: route.query.token,
    dob: store.state.global.dob,
  });
  store.dispatch('global/setName', res.fullName, { root: true });
  localStorage.setItem(encodeURIComponent(route.query.token), JSON.stringify([store.state.global.dob, res.fullName]));
}
getFamilyData();

watch(date, (val) => {
  if (val) {
    dateEmpty.value = false;
  } else {
    dateEmpty.value = true;
  }
});

</script>

<script>
export default {
  name: 'ResultPageView',
};
</script>

<style lang="scss" scoped>
.list-accordion {
  font-size: 14px;
  padding: 8px;

  li {
    padding-left: 24px;
    position: relative;

    &:not(:last-child) {
      margin-bottom: 16px;
    }

    &::before {
      content: '';
      width: 20px;
      height: 20px;
      border-radius: 4px;
      border: 1px solid #E0E0E0;
      display: block;
      position: absolute;
      left: 4px;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
}
</style>
